import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../context/AuthContext';
import {usePeriod} from '../context/PeriodContext';
import {useAppNavigation} from '../context/NavigationContext';
import {API_BASE_URL} from '../config/apiConfig';
import {Contract, getContractsByPeriodKey} from '../services/payrollService';
import {BranchEvidenceSummary, getWorkEvidenceSummary} from '../services/workEvidenceService';
import {formatPeriodLabel, parsePeriodKey} from '../utils/periodLabel';
import type {PayrollCalculation} from './usePayrollRun';
import type {WorkEvidenceRecord} from '../components/WorkEvidence/WorkEvidenceApprovalTableComponent';
import {buildPayrollWorkflow} from '../utils/payrollWorkflow';

// ─── Types ────────────────────────────────────────────────────────────────────

export type {WizardStep, WizardStepKey} from '../utils/payrollWorkflow';

export interface DashboardStats {
    employeeCount: number;
    branchCount: number;
    gross: number | null;
    netPay: number | null;
    employerCost: number | null;
    /** False while no payroll snapshot/calculation exists yet for the selected period+branch. */
    financialsAvailable: boolean;
    /** True once the period is FINALIZED: figures are the stored, final ones; otherwise preliminary (live). */
    financialsFinal: boolean;
    loading: boolean;
}

export interface AttentionItem {
    id: string;
    icon: 'evidence' | 'contracts' | 'payChanges';
    tone: 'error' | 'warning' | 'info';
    title: string;
    description: string;
    onClick: () => void;
}

export interface CostHistoryPoint {
    periodKey: string;
    label: string;
    gross: number;
    cost: number;
}

export interface BranchStatusRow {
    branchId: string;
    branchName: string;
    employeeCount: number;
    evidenceTotal: number;
    evidencePending: number;
    evidenceSubmitted: number;
    evidenceApproved: number;
    evidenceInPreparation: number;
    onView: () => void;
}

interface BranchEvidenceStats {
    total: number;
    pending: number;
    submitted: number;
    approved: number;
    inPreparation: number;
}

interface PayrollRunApiResponse {
    totalRecords: number;
    payrollCalculations: PayrollCalculation[];
    totalGross: number;
    totalNetPay: number;
}

interface WorkEvidenceApiResponse {
    totalRecords: number;
    workEvidences: WorkEvidenceRecord[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatEUR(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    try {
        return value.toLocaleString('de-DE', {style: 'currency', currency: 'EUR'});
    } catch {
        return `€${value.toFixed(2)}`;
    }
}

// A work-evidence record is "pending" whenever it isn't approved yet — mirrors the Work
// Evidence page's own definition (StatusChip.isApprovedStatus). Records that haven't been
// submitted at all have status === null, which must also count as pending; comparing
// against a single literal status (e.g. "SUBMITTED") under-counts pending evidence and was
// the cause of the dashboard showing "Completed" while the Work Evidence page still showed
// "In preparation" for the same branch/period.

export async function fetchPayrollRun(token: string, periodKey: string, branch: string): Promise<PayrollRunApiResponse | null> {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/run?branch=${branch}&periodKey=${periodKey}&preferStored=true`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        });
        if (!response.ok) return null;
        return await response.json();
    } catch {
        return null;
    }
}

export async function fetchWorkEvidence(token: string, periodKey: string, branchId: string): Promise<WorkEvidenceApiResponse | null> {
    try {
        const response = await fetch(`${API_BASE_URL}/api/work-evidence/get?periodKey=${periodKey}&branch=${branchId}`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        });
        if (!response.ok) return null;
        return await response.json();
    } catch {
        return null;
    }
}

async function fetchMonthlyChangeCount(token: string, periodKey: string): Promise<number> {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/monthly/getColumns?periodKey=${periodKey}`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        });
        if (!response.ok) return 0;
        const data = await response.json();
        return Array.isArray(data.addedColumns) ? data.addedColumns.length : 0;
    } catch {
        return 0;
    }
}

const MAX_COST_HISTORY_POINTS = 6;
const CONTRACT_ENDING_SOON_DAYS = 30;

export function useDashboard() {
    const {t} = useTranslation();
    const {token, user} = useAuth();
    const {
        selectedPeriod, selectedPeriodValue,
        allPeriods, branches, optionsLoading,
        dashboardBranch: selectedBranch, dashboardBranchName, setDashboardBranch,
    } = usePeriod();
    const {navigate} = useAppNavigation();

    const [contracts, setContracts] = React.useState<Contract[]>([]);
    const [payrollRun, setPayrollRun] = React.useState<PayrollRunApiResponse | null>(null);
    const [financialsLoading, setFinancialsLoading] = React.useState<boolean>(false);

    const [branchEvidence, setBranchEvidence] = React.useState<Record<string, BranchEvidenceStats>>({});
    const [monthlyChangeCount, setMonthlyChangeCount] = React.useState<number>(0);

    const [costHistory, setCostHistory] = React.useState<CostHistoryPoint[]>([]);
    const [costHistoryLoading, setCostHistoryLoading] = React.useState<boolean>(false);

    const isPeriodLocked = selectedPeriodValue?.toUpperCase().includes('LOCKED') ?? false;

    const selectedPeriodLabel = React.useMemo(() => {
        const period = allPeriods.find((p) => p.periodKey === selectedPeriod);
        return period ? formatPeriodLabel(t, period.periodKey, period.value) : '';
    }, [allPeriods, selectedPeriod, t]);

    // Periods + branches are now fetched once, globally, by PeriodProvider (so the top toolbar
    // selectors and this hook both read the same data without duplicating the request).
    const loading = optionsLoading;

    // ─── Per-period data: contracts, payroll run totals, evidence status, pay changes ────────────
    React.useEffect(() => {
        if (!token || !selectedPeriod) return;
        let cancelled = false;

        getContractsByPeriodKey(token, selectedPeriod)
            .then((response) => {
                if (!cancelled) setContracts(response.contracts ?? []);
            })
            .catch(() => {
                if (!cancelled) setContracts([]);
            });

        fetchMonthlyChangeCount(token, selectedPeriod).then((count) => {
            if (!cancelled) setMonthlyChangeCount(count);
        });

        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod]);

    // Financial totals for the selected period + branch filter (real backend calculation).
    React.useEffect(() => {
        if (!token || !selectedPeriod) return;
        let cancelled = false;
        setFinancialsLoading(true);
        fetchPayrollRun(token, selectedPeriod, selectedBranch).then((data) => {
            if (!cancelled) {
                setPayrollRun(data);
                setFinancialsLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod, selectedBranch]);

    // Work evidence status per branch for the selected period (drives attention list + branch table).
    React.useEffect(() => {
        if (!token || !selectedPeriod || branches.length === 0) return;
        let cancelled = false;
        getWorkEvidenceSummary(token, selectedPeriod)
            .catch((): BranchEvidenceSummary[] => [])
            .then((rows) => {
                if (cancelled) return;
                const map: Record<string, BranchEvidenceStats> = {};
                // Branches without contracts come back absent: treat as 0/0.
                branches.forEach((branch) => {
                    map[branch.id] = {total: 0, pending: 0, submitted: 0, approved: 0, inPreparation: 0};
                });
                rows.forEach((r) => {
                    map[r.branchId] = {
                        total: r.total,
                        pending: r.pending,
                        submitted: r.submitted,
                        approved: r.approved,
                        inPreparation: r.inPreparation,
                    };
                });
                setBranchEvidence(map);
            });
        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod, branches]);

    // Cost history: best-effort real figures pulled from the last few payroll periods.
    // TODO(backend): replace with a dedicated lightweight summary endpoint (e.g.
    // GET /api/payroll/summary?periodKeys=...) instead of re-running full payroll
    // calculations per period just to plot this chart.
    React.useEffect(() => {
        if (!token || allPeriods.length === 0) return;
        let cancelled = false;
        const sortedKeys = [...allPeriods]
            .map((p) => ({...p, parsed: parsePeriodKey(p.periodKey)}))
            .filter((p) => p.parsed !== null)
            .sort((a, b) => (a.parsed!.year * 12 + a.parsed!.month) - (b.parsed!.year * 12 + b.parsed!.month))
            .slice(-MAX_COST_HISTORY_POINTS);

        setCostHistoryLoading(true);
        Promise.all(
            sortedKeys.map((p) =>
                fetchPayrollRun(token, p.periodKey, 'All').then((data) => {
                    if (!data) return null;
                    const cost = (data.payrollCalculations ?? []).reduce((sum, e) => sum + (e.employerCostTotal ?? 0), 0);
                    return {
                        periodKey: p.periodKey,
                        label: t(`common.month.${p.parsed!.month}`),
                        gross: data.totalGross ?? 0,
                        cost,
                    } as CostHistoryPoint;
                }),
            ),
        ).then((results) => {
            if (!cancelled) setCostHistory(results.filter((r): r is CostHistoryPoint => r !== null));
        }).finally(() => {
            if (!cancelled) setCostHistoryLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, allPeriods]);

    // ─── Handlers ─────────────────────────────────────────────────────────────────────────────────
    // Period/branch selection now lives in the global top toolbar (see PeriodBranchToolbarSelector);
    // this hook only reads the resulting `selectedPeriod`/`selectedBranch` from PeriodContext.

    const goToPayroll = React.useCallback(() => navigate('Payroll'), [navigate]);
    const goToContracts = React.useCallback(() => navigate('Contracts'), [navigate]);
    const goToWorkEvidenceApprovals = React.useCallback(() => navigate('Work Evidence Approvals'), [navigate]);
    const goToWorkEvidence = React.useCallback(() => navigate('Work Evidence'), [navigate]);
    const goToMonthlyChanges = React.useCallback(() => navigate('Monthly Changes'), [navigate]);

    // ─── Derived: stat cards ──────────────────────────────────────────────────────────────────────

    const employerCostTotal = React.useMemo(
        () => (payrollRun?.payrollCalculations ?? []).reduce((sum, e) => sum + (e.employerCostTotal ?? 0), 0),
        [payrollRun],
    );

    const isSelectedPeriodFinalized = React.useMemo(
        () => allPeriods.find((p) => p.periodKey === selectedPeriod)?.workflowStage === 'FINALIZED',
        [allPeriods, selectedPeriod],
    );

    const stats: DashboardStats = {
        employeeCount: payrollRun?.payrollCalculations?.length ?? contracts.length,
        branchCount: branches.length,
        gross: payrollRun ? payrollRun.totalGross : null,
        netPay: payrollRun ? payrollRun.totalNetPay : null,
        employerCost: payrollRun ? employerCostTotal : null,
        financialsAvailable: payrollRun !== null,
        financialsFinal: isSelectedPeriodFinalized,
        loading: financialsLoading,
    };

    // ─── Derived: wizard steps ────────────────────────────────────────────────────────────────────
    // Approval/Finalization currently collapse onto the single OPEN/LOCKED period status the
    // backend exposes today; a dedicated payroll workflow-stage field would let us tell them apart.

    const pendingEvidenceTotal = React.useMemo(
        () => Object.values(branchEvidence).reduce((sum, v) => sum + v.pending, 0),
        [branchEvidence],
    );
    const totalEvidenceTotal = React.useMemo(
        () => Object.values(branchEvidence).reduce((sum, v) => sum + v.total, 0),
        [branchEvidence],
    );
    // Only SUBMITTED records ("Send for approval" done) can be acted on in Work Evidence Approvals.
    const submittedEvidenceTotal = React.useMemo(
        () => Object.values(branchEvidence).reduce((sum, v) => sum + v.submitted, 0),
        [branchEvidence],
    );
    const notSentEvidenceTotal = React.useMemo(
        () => Object.values(branchEvidence).reduce((sum, v) => sum + v.inPreparation, 0),
        [branchEvidence],
    );

    const {
        steps: wizardSteps,
        activeStepIndex,
        isComplete: isWizardComplete,
        statusLabel: wizardStatusLabel,
    } = buildPayrollWorkflow(t, {
        hasContracts: contracts.length > 0,
        evidenceTotal: totalEvidenceTotal,
        evidencePending: pendingEvidenceTotal,
        financialsAvailable: isSelectedPeriodFinalized,
        isPeriodLocked,
    });

    // ─── Derived: attention list ──────────────────────────────────────────────────────────────────

    const contractsEndingSoon = React.useMemo(() => {
        const now = Date.now();
        const horizon = now + CONTRACT_ENDING_SOON_DAYS * 24 * 60 * 60 * 1000;
        return contracts.filter((c) => {
            if (!c.endDate || c.status?.toUpperCase() === 'TERMINATED') return false;
            const end = new Date(c.endDate).getTime();
            return !Number.isNaN(end) && end >= now && end <= horizon;
        }).length;
    }, [contracts]);

    const attentionItems: AttentionItem[] = React.useMemo(() => {
        const items: AttentionItem[] = [];
        if (submittedEvidenceTotal > 0) {
            items.push({
                id: 'pending-evidence',
                icon: 'evidence',
                tone: 'error',
                title: t('dashboard.attention.pendingEvidence.title', {count: submittedEvidenceTotal}),
                description: t('dashboard.attention.pendingEvidence.description', {period: selectedPeriodLabel}),
                onClick: goToWorkEvidenceApprovals,
            });
        }
        if (notSentEvidenceTotal > 0) {
            items.push({
                id: 'unsent-evidence',
                icon: 'evidence',
                tone: 'warning',
                title: t('dashboard.attention.unsentEvidence.title', {count: notSentEvidenceTotal}),
                description: t('dashboard.attention.unsentEvidence.description', {period: selectedPeriodLabel}),
                onClick: goToWorkEvidence,
            });
        }
        if (contractsEndingSoon > 0) {
            items.push({
                id: 'contracts-ending',
                icon: 'contracts',
                tone: 'error',
                title: t('dashboard.attention.contractsEnding.title', {count: contractsEndingSoon}),
                description: t('dashboard.attention.contractsEnding.description'),
                onClick: goToContracts,
            });
        }
        if (monthlyChangeCount > 0) {
            items.push({
                id: 'pay-changes',
                icon: 'payChanges',
                tone: 'info',
                title: t('dashboard.attention.payChanges.title', {count: monthlyChangeCount}),
                description: t('dashboard.attention.payChanges.description'),
                onClick: goToMonthlyChanges,
            });
        }
        return items;
    }, [submittedEvidenceTotal, notSentEvidenceTotal, contractsEndingSoon, monthlyChangeCount, selectedPeriodLabel, t, goToWorkEvidenceApprovals, goToWorkEvidence, goToContracts, goToMonthlyChanges]);

    // ─── Derived: branch status table ────────────────────────────────────────────────────────────
    // Note: the reference design also shows a "responsible person" per branch; there is no such
    // field in the current /api/branches/get payload, so that column is intentionally omitted
    // here until the backend exposes a branch owner/contact.

    const employeesByBranch = React.useMemo(() => {
        const map: Record<string, number> = {};
        (payrollRun?.payrollCalculations ?? []).forEach((e) => {
            map[e.branch] = (map[e.branch] ?? 0) + 1;
        });
        return map;
    }, [payrollRun]);

    const branchStatusRows: BranchStatusRow[] = React.useMemo(
        () => branches.map((branch) => ({
            branchId: branch.id,
            branchName: branch.name,
            employeeCount: employeesByBranch[branch.name] ?? 0,
            evidenceTotal: branchEvidence[branch.id]?.total ?? 0,
            evidencePending: branchEvidence[branch.id]?.pending ?? 0,
            evidenceSubmitted: branchEvidence[branch.id]?.submitted ?? 0,
            evidenceApproved: branchEvidence[branch.id]?.approved ?? 0,
            evidenceInPreparation: branchEvidence[branch.id]?.inPreparation ?? 0,
            onView: () => {
                navigate('Work Evidence Approvals');
            },
        })),
        [branches, employeesByBranch, branchEvidence, navigate],
    );

    return {
        userName: user,
        allPeriods,
        selectedPeriod,
        selectedPeriodValue,
        selectedPeriodLabel,
        branches,
        selectedBranch,
        selectedBranchName: dashboardBranchName,
        setDashboardBranch,
        loading,
        isPeriodLocked,
        stats,
        wizardSteps,
        activeStepIndex,
        wizardStatusLabel,
        isWizardComplete,
        attentionItems,
        costHistory,
        costHistoryLoading,
        branchStatusRows,
        onContinuePreparation: goToPayroll,
    };
}
